import Icon from '@/components/ui/AppIcon';

interface LocationPrice {
  id: string;
  district: string;
  region: string;
  price: number;
  difference: number;
  explanation: string;
}

interface LocationPricingProps {
  fishName: string;
  locations: LocationPrice[];
}

const LocationPricing = ({ fishName, locations }: LocationPricingProps) => {
  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-sm p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-md">
          <Icon name="MapPinIcon" size={20} className="text-primary" />
        </div>
        <div>
          <h2 className="font-heading font-bold text-xl text-foreground">Location-Based Pricing</h2>
          <p className="text-sm text-muted-foreground">{fishName} prices across districts</p>
        </div>
      </div>

      <div className="space-y-4">
        {locations.map((location) => (
          <div
            key={location.id}
            className="border border-border rounded-md p-4 transition-smooth hover:shadow-elevation-sm hover:border-primary/30"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-heading font-semibold text-base text-foreground">
                    {location.district}
                  </h3>
                  <span className="text-xs font-caption text-muted-foreground">
                    {location.region}
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-data font-bold text-2xl text-primary">
                    ₹{location.price.toFixed(2)}
                  </span>
                  <span className="text-sm text-muted-foreground">/kg</span>
                  {location.difference !== 0 && (
                    <span className={`flex items-center gap-1 text-sm font-medium ${
                      location.difference > 0 ? 'text-error' : 'text-success'
                    }`}>
                      <Icon 
                        name={location.difference > 0 ? 'ArrowUpIcon' : 'ArrowDownIcon'} 
                        size={14} 
                      />
                      {Math.abs(location.difference).toFixed(1)}%
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2 p-3 bg-muted rounded-md">
              <Icon name="InformationCircleIcon" size={16} className="text-primary flex-shrink-0 mt-0.5" />
              <p className="text-sm text-foreground">{location.explanation}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-md">
        <div className="flex items-start gap-3">
          <Icon name="ShieldCheckIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-heading font-semibold text-sm text-foreground mb-1">
              Transparent Pricing Policy
            </h4>
            <p className="text-sm text-muted-foreground">
              Price variations reflect actual logistics costs, local demand, and supply chain factors across Haryana. FishBite maintains transparency in all pricing decisions to ensure fair market practices.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationPricing;